import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { KanbanCard } from "../../../model/kanban/KanbanCard";
import { standardDate } from "../../../utils/Date";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useAppSelector } from "../../../redux/store";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { faRoad } from "@fortawesome/free-solid-svg-icons/faRoad";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { faDollarSign } from "@fortawesome/free-solid-svg-icons/faDollarSign";
import { faCalendarDay } from "@fortawesome/free-solid-svg-icons/faCalendarDay";
import { renderHighlight } from "../utils/searchHighlight";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faNoteSticky } from "@fortawesome/free-regular-svg-icons/faNoteSticky";
import isEmpty from "lodash/isEmpty";
import { faComment } from "@fortawesome/free-regular-svg-icons/faComment";

const IconWrapper = styled(Flex)`
  vertical-align: middle;
  margin-inline-end: ${Spacing.xs};
  display: inline-flex;
  justify-content: center;
  width: 1rem;
`;

type Props = {
  kanbanCard: Partial<KanbanCard> & {
    kanbanDoc?: { name?: string };
    kanbanColDoc?: { name?: string };
    entryDoc?: { assigneeIds?: string; fields?: Record<string, string> };
  };
};

export const SearchCardKanbanCard: React.FC<Props> = ({ kanbanCard }) => {
  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const parentEntryFields = kanbanCard?.entryDoc?.fields;

  const contactName =
    parentEntryFields?.email ||
    parentEntryFields?.phone ||
    parentEntryFields?.name;

  return (
    <Flex flexDirection="column" gap="s" flex={1}>
      <Text className="atira-search-text">
        <IconWrapper>
          <AtiraIcon icon={faCheck} />
        </IconWrapper>
        <strong>{t("common.name")}: </strong>
        <span>{renderHighlight(kanbanCard, "title") || kanbanCard.title}</span>
      </Text>

      <Text className="atira-search-text">
        <IconWrapper>
          <AtiraIcon icon={faRoad} />
        </IconWrapper>
        <strong>{t("common.kanban")}: </strong>
        <span>
          {kanbanCard.kanbanDoc?.name} / {kanbanCard.kanbanColDoc?.name}
        </span>
      </Text>

      <Text className="atira-search-text">
        <IconWrapper>
          <AtiraIcon icon={faUser} />
        </IconWrapper>
        <strong>{t("common.contact")}: </strong>
        <bdi>{contactName}</bdi>
      </Text>

      <Text className="atira-search-text">
        <IconWrapper>
          <AtiraIcon icon={faDollarSign} />
        </IconWrapper>
        <strong>{t("common.amount")}: </strong>
        <span>
          {renderHighlight(kanbanCard, "amount") ||
            `${kanbanCard.amount || 0}$`}
        </span>
      </Text>

      {kanbanCard?.note ? (
        <Text className="atira-search-text">
          <IconWrapper>
            <AtiraIcon icon={faNoteSticky} />
          </IconWrapper>
          <strong>{t("common.note")}: </strong>
          <span>{renderHighlight(kanbanCard, "note") || kanbanCard.note}</span>
        </Text>
      ) : null}

      {kanbanCard?.closingDate ? (
        <Text className="atira-search-text">
          <IconWrapper>
            <AtiraIcon icon={faCalendarDay} />
          </IconWrapper>
          <strong>{t("common.deadline")}: </strong>
          <span>{standardDate<string>(kanbanCard.closingDate, user)}</span>
        </Text>
      ) : null}

      {!isEmpty(kanbanCard.customFields) ? (
        <Flex flexDirection="column" gap="xs">
          <Text className="atira-search-text">
            <IconWrapper>
              <AtiraIcon icon={faComment} color="main" />
            </IconWrapper>
            <strong>{t("common.customFields")}</strong>
          </Text>
          {Object.entries(kanbanCard.customFields!).map(
            ([fieldName, value]) => (
              <Text key={fieldName} className="atira-search-text">
                <Flex gap="xs" marginInlineStart="m">
                  <strong>{value.label}: </strong>
                  <bdi>{value.value}</bdi>
                </Flex>
              </Text>
            ),
          )}
        </Flex>
      ) : null}
    </Flex>
  );
};
